// React
import { memo, useCallback, useEffect, useState } from "react";

// Servisni sloj
import { updateTenantSubscriptionAPI } from "services/api/subscriptions/api";
import { initialSubscriptionStepForm } from "services/api/subscriptions/consts";
import { SubscriptionStepFormValues } from "services/api/subscriptions/types";
import { TenantCreateFormValues } from "services/api/tenants/types";

// UI
import { Button } from "ui/components/Button/Button";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import invoiceAnimation from "ui/theme/animations/invoice.json";

// Lokalne komponente
import TenantSubscriptionStep from "ui/modules/tenants/pages/TenantsSearchPage/components/TenantEditCard/components/TenantSubscriptionStep/TenantSubscriptionStep";

// Tipovi
import { SubscriptionEditCardProps } from "./types";

/**
 * Izmjena pretplate.
 *
 * Obrazac je isti koji stoji u čarobnjaku za unos klijenta, samo se puni iz potpisane pretplate.
 * Time se pravila i izgled ne mogu razići; korak sam zna da je u izmjeni i tada ne nudi početak
 * pretplate ni probni rok, jer su to odluke s potpisa koje backend ne mijenja.
 */
const SubscriptionEditCard: React.FC<SubscriptionEditCardProps> = (
  props: SubscriptionEditCardProps
) => {
  const { show, tenantId, subscription, vehicleCount, onSuccess, onClose } = props;

  const [formValues, setFormValues] = useState<SubscriptionStepFormValues>(
    initialSubscriptionStepForm
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Modal ostaje montiran, pa se svako otvaranje mora vratiti na spremljeno stanje.
  useEffect(() => {
    if (!show) return;

    setFormValues({
      ...initialSubscriptionStepForm,
      packCodes: subscription.packCodes,
      billingPeriod: subscription.billingPeriod,
      startsOn: subscription.startsOn,
      withTrial: subscription.trialEndsOn !== null,
      estimatedVehicles: vehicleCount,
    });
  }, [show, subscription, vehicleCount]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      await updateTenantSubscriptionAPI(tenantId, {
        packCodes: formValues.packCodes,
        billingPeriod: formValues.billingPeriod,
        pricingSchemeId: formValues.pricingSchemeId,
        effectiveFrom: null,
      });

      onSuccess();
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsSaving(false);
    }
  }, [tenantId, formValues, onSuccess]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="full"
      position="side"
      intro={{
        content: (
          <ModalIntro
            animation={invoiceAnimation}
            title="Izmijenite pretplatu klijenta"
            description="Promijenite pakete, način plaćanja ili cjenovnu grupu. Izmjena vrijedi od danas, a ono što je vrijedilo dosad ostaje zabilježeno."
            show={show}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title="Izmjena pretplate"
        onHide={onClose}
      />

      <ModalBody fixedFooterSpacing={false}>
        <TenantSubscriptionStep
          tenantValues={{ id: tenantId } as TenantCreateFormValues}
          formValues={formValues}
          setFormValues={setFormValues}
          existingSubscription={subscription}
          isEditing
        />
      </ModalBody>

      <div className="flex justify-end gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50">
        <Button variant="outlined" children="Odustani" onClick={onClose} />

        <Button
          variant="primary"
          children="Spremi izmjenu"
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </Modal>
  );
};

export default memo(SubscriptionEditCard);
